import * as Sentry from "@sentry/browser";
import { cloneDeep } from "lodash-es";
import percentRound from "percent-round";
import TagManager from "react-gtm-module";
import { Address, Hash } from "viem";
import { getAccount, waitForTransaction } from "wagmi/actions";
import { Amount } from "../../types/amount";
import { OrderStatus } from "../../types/orderStatus";
import { WrapType } from "../../types/wrapQuote";
import { getWrappedTokenAddress } from "../../utils/chainUtils";
import { getOrderType } from "../../utils/orderTypeUtils";
import { getPermitFromStore, getPermitToUse } from "../../utils/permitUtils";
import { INITIAL_RECEIVER_STATE } from "../../utils/swapAndSendUtils";
import { removeUrlParam, updateUrlParams, URL_KEYS } from "../../utils/urlUtils";
import { attachQuoteID, attachUserAddressAndWallet } from "../../utils/userUtils";
import { ActivityStore, INITIAL_STATE_ACTIVITY } from "../activity/ActivityStore";
import { getHistory } from "../activity/getHistory";
import { GlobalStore, INITIAL_STATE, initialWrapQuote } from "./GlobalStore";
import { getBalances } from "./getBalances";
import { getRoles } from "./getGuildRoles";
import { getQuoteDebounced } from "./getQuote";
import { getZerionNft } from "./getZerionNftImage";
import { sendSignedOrder } from "./signOrder";
import { getAllTokenLists, TokenList } from "./getAllTokenLists";
import { areTokensIdentical, calculateUsdTradeSurplus } from "../../utils/orderUtils";
import { AMOUNT_TO_SHOW_TRADE_SURPLUS, NATIVE_TOKEN_ADDRESS } from "../../config/consts";
import { getPermit2Allowances } from "./getPermit2Allowance";
import { isAddressEqual } from "../../utils/tokenUtils";
import { loadCoinGeckoTokenList } from "./loadCoinGeckoTokenList";

export const attachReactions = (): void => {
  GlobalStore.createReaction(
    store => {
      return {
        orderType: getOrderType(store.takerTokens, store.makerTokens),
        userAddress: store.address,
        takerTokens: store.takerTokens,
        tokenLists: store.tokenLists,
        network: store.network,
      };
    },
    watched => {
      const { orderType, userAddress, takerTokens, tokenLists, network } = watched;
      if (orderType && userAddress && takerTokens.length > 0 && tokenLists && network) {
        const tokenList = tokenLists[network.id];
        getPermitToUse(orderType, userAddress as `0x${string}`, takerTokens, tokenList, network)
          .then(permitType => {
            console.log(`Reaction Determined permit type to be ${permitType}`);
            GlobalStore.update(s => {
              s.permitType = permitType;
            });
          })
          .catch(e => {
            console.error(`Could not determine permit type`, e);
          });
      }
    },
    {
      runNowWithSideEffects: true,
    }
  );

  GlobalStore.createReaction(
    store => store.address,
    (address, draft, originalStore) => {
      if (address) {
        // Reset all user-related data in GlobalStore when address changes
        draft.zerionNft = "";
        ActivityStore.update(s => {
          const { data, isLoading, error } = INITIAL_STATE_ACTIVITY.history;
          s.history = { data, isLoading, error };
        });
        draft.ensName = "";
        draft.ensAvatar = "";
        draft.zerionNft = "";
        draft.userRoles = [];

        void getZerionNft.run(address);
        const walletConnector = getAccount().connector;
        attachUserAddressAndWallet(address, walletConnector);
        // Updated to check store.tokenLists rather than draft it seemed like this condition would never pass
        if (originalStore.tokenLists) {
          const tokenListOriginalStore = originalStore.tokenLists[originalStore.network.id];
          const tokenListDraft = draft.tokenLists?.[draft.network.id];
          void getBalances.run({
            address: address as Address,
            tokenList: cloneDeep(tokenListOriginalStore),
            chain: cloneDeep(originalStore.network),
          });
          void getPermit2Allowances.run({
            address: address as Address,
            tokenList: cloneDeep(tokenListDraft),
          });
        }

        const TODAY_TIMESTAMP = new Date().getTime() * 1e6;
        void getHistory.run({
          address: draft.address as Address,
          timestamp: TODAY_TIMESTAMP,
          previousResult: undefined,
        });

        void getRoles.run({ userAddress: address as Address });
      }
    }
  );

  GlobalStore.createReaction(
    store => store.network,
    (network, draft, _, lastWatched) => {
      const address = draft.address.toString();
      const tokenList = draft.tokenLists?.[draft.network.id];
      void loadCoinGeckoTokenList.run({ chain: network });

      if (network && network.name !== lastWatched.name && tokenList != null) {
        // Refresh prices, tokens and balances to get new applicable tokens
        void getBalances
          .run({
            address: address as `0x${string}`,
            tokenList: cloneDeep(tokenList),
            chain: network,
          })
          .catch(err => {
            const errMsg = `Could not get balances after a network switch`;
            console.error(errMsg, err);
            Sentry.captureException(errMsg, { extra: { err: errMsg } });
          });

        // Set network default token to top token.
        // TODO: use resetState function, attempted but didn't work as intended so using this still for now
        draft.takerTokens = [];
        draft.makerTokens = [];
        draft.quote = undefined;
        draft.isRequestingQuote = false;
        draft.order = undefined;
        draft.receiver = INITIAL_RECEIVER_STATE;
        draft.orderError = undefined;
        draft.wrapQuote = initialWrapQuote;
        draft.receiverError = "";
        draft.isSending = false;
        draft.quoteError = undefined;
      }
    },
    {
      runNowWithSideEffects: true,
    }
  );
  // Update URL Params on change
  GlobalStore.createReaction(
    store => {
      return {
        makerTokens: store.makerTokens.map(e => e.tokenAddress),
        takerTokens: store.takerTokens.map(e => e.tokenAddress),
      };
    },
    ({ makerTokens, takerTokens }) => {
      const network = GlobalStore.getRawState().network;
      const tokenList = GlobalStore.getRawState().tokenLists?.[network.id];

      const getTokenTicker = (tokenAddress: Hash, tokenList: TokenList) => {
        return tokenList?.[tokenAddress]?.ticker ?? tokenAddress;
      };

      // TODO TOKENS: Figure out how URL params are going to work with contract addresses
      const sortedMakerTokens = [...makerTokens]
        .map(e => getTokenTicker(e, tokenList ?? {}))
        .sort((a, b) => a.localeCompare(b));
      const serializedMakerTokenSymbols = sortedMakerTokens.join(",");
      const sortedTakerTokens = [...takerTokens].sort((a, b) => a.localeCompare(b));
      const serializedTakerTokenSymbols = sortedTakerTokens
        .map(e => getTokenTicker(e, tokenList ?? {}))
        .join(",");

      if (serializedMakerTokenSymbols.length === 0) {
        removeUrlParam(URL_KEYS.BUY);
      } else {
        updateUrlParams(URL_KEYS.BUY, serializedMakerTokenSymbols, window.location.pathname);
      }
      if (serializedTakerTokenSymbols.length === 0) {
        removeUrlParam(URL_KEYS.SELL);
      } else {
        updateUrlParams(URL_KEYS.SELL, serializedTakerTokenSymbols, window.location.pathname);
      }
    }
  );

  // Update URL Params on network change
  GlobalStore.createReaction(
    store => store.network,
    network => {
      updateUrlParams(URL_KEYS.NETWORK, network.name.toLowerCase(), window.location.pathname);
    }
  );

  // Rounds percentages
  GlobalStore.createReaction(
    store => store.makerTokens,
    (makerTokens, draft, original, lastWatched) => {
      // Only rebalance when tokens are added or removed
      if (lastWatched.length !== makerTokens.length) {
        // Recalculate percentages to avoid irrational numbers
        // i.e. convert [33.3(3), 33.3(3), 33.3(3)] to [34, 33, 33]
        const roundedPercentages = percentRound(makerTokens.map(entry => entry.pct));
        draft.makerTokens = draft.makerTokens.map((entry, index) => {
          entry.pct = roundedPercentages[index];
          return entry;
        });
      }
    }
  );

  // Requesting quote reaction
  GlobalStore.createReaction(
    store => {
      return {
        orderType: getOrderType(store.takerTokens, store.makerTokens),
        makerTokens: store.makerTokens,
        takerTokens: store.takerTokens,
        network: store.network,
        address: store.address,
        tokens: store.tokenLists,
        orderStatus: store.orderStatus,
        receiver: store.receiver,
        permits: store.permits,
      };
    },
    (watched, draft) => {
      const takerTokenHasAnAmount = !draft.takerTokens.some(entry => entry.amount.value.isZero());
      const makerTokenSelected = draft.makerTokens.length > 0;
      const takerArray = draft.takerTokens.map(e => e.tokenAddress);
      const makerArray = draft.makerTokens.map(e => e.tokenAddress);
      const isUnwrapping =
        takerArray.some(address =>
          isAddressEqual(address, getWrappedTokenAddress(draft.network))
        ) && makerArray.some(address => isAddressEqual(address, NATIVE_TOKEN_ADDRESS));
      const isWrapping =
        makerArray.some(address =>
          isAddressEqual(address, getWrappedTokenAddress(draft.network))
        ) && takerArray.some(address => isAddressEqual(address, NATIVE_TOKEN_ADDRESS));
      const quotableOrderState =
        draft.orderStatus === OrderStatus.ENTRY || draft.orderStatus === OrderStatus.QUOTING;

      if (isWrapping || isUnwrapping) {
        const wrappingOrUnwrapping = isWrapping ? WrapType.Wrapping : WrapType.Unwrapping;
        const amount = draft.takerTokens[0].amount;
        const newWrapQuote = {
          type: wrappingOrUnwrapping,
          amount: amount,
          max: true,
          gas: Amount.ZERO,
          hash: draft.wrapQuote?.hash,
          warning: undefined,
          error: undefined,
        };
        draft.wrapQuote = newWrapQuote;
      }

      if (
        takerTokenHasAnAmount &&
        makerTokenSelected &&
        watched.orderType &&
        watched.tokens != null &&
        quotableOrderState &&
        !isWrapping &&
        !isUnwrapping
      ) {
        getQuoteDebounced({
          orderType: watched.orderType,
          takerTokens: watched.takerTokens,
          makerTokens: watched.makerTokens,
          network: watched.network,
          address: watched.address as `0x${string}`,
          tokenList: watched.tokens[watched.network.id],
          receiver: watched.receiver,
          permitType: draft.permitType,
          forceRouteType: undefined, //Reaction handles order entry changes - so we leave as is - to force a BEST quote fetch.
        });
        // Set flag so we remember the current route for the quote
      }
    },
    {
      runNowWithSideEffects: true,
    }
  );

  // Wait for order tx
  GlobalStore.createReaction(
    store => store.order,
    (watched, draft) => {
      if (watched?.txHash) {
        // Add alert for when a user try's to exit whilst they have put a trade through
        // This is an extra precaution to help with incorrect balances displaying on the UI.
        const clonedOrder = cloneDeep(watched);
        const clonedQuote = cloneDeep(draft.quote);
        window.onbeforeunload = function () {
          return true;
        };
        waitForTransaction({
          hash: watched.txHash as `0x${string}`,
        })
          .then(receipt => {
            if (receipt.transactionHash && receipt.status) {
              console.log("Has trade surplus:", calculateUsdTradeSurplus(clonedOrder, clonedQuote));
              if (
                calculateUsdTradeSurplus(clonedOrder, clonedQuote).value.gt(
                  AMOUNT_TO_SHOW_TRADE_SURPLUS
                )
              ) {
                GlobalStore.update(s => {
                  s.permits = INITIAL_STATE.permits;
                  s.orderStatus = OrderStatus.TRADE_SURPLUS;
                });
              } else {
                GlobalStore.update(s => {
                  s.permits = INITIAL_STATE.permits;
                  s.orderStatus = OrderStatus.COMPLETED;
                });
              }
            } else {
              const errMsg = `There was an error with the tx receipt.`;
              console.error(errMsg, receipt);
              Sentry.captureException(errMsg, { extra: { err: JSON.stringify(receipt) } });
              GlobalStore.update(s => {
                // Reset permits so we don't send them anymore
                s.permits = INITIAL_STATE.permits;
                s.orderStatus = OrderStatus.ERROR;
              });
            }
          })
          .catch(e => {
            const errMsg = `Order Tx did not complete`;
            console.log(errMsg);
            console.error(e);
            Sentry.captureException(errMsg, { extra: { err: e } });
            GlobalStore.update(s => {
              // Reset permits so we don't send them anymore
              s.permits = INITIAL_STATE.permits;
              s.orderStatus = OrderStatus.ERROR;
            });
          });
      }
    },
    {
      runNowWithSideEffects: true,
    }
  );

  // Rounds percentages
  GlobalStore.createReaction(
    store => store.quote,
    (quote, draft, original, lastWatched) => {
      if (quote) {
        attachQuoteID(quote.quoteId);
      } else if (lastWatched?.quoteId) {
        draft.orderStatus = OrderStatus.ENTRY;
        return;
      }
      if (lastWatched?.quoteId !== quote?.quoteId) {
        if (draft.orderStatus === OrderStatus.ENTRY) {
          draft.orderStatus = OrderStatus.QUOTING;
        }
      }
    }
  );

  GlobalStore.createReaction(
    store => store.signedMessage,
    (signedMessage, draft) => {
      if (draft.signedMessage) {
        void sendSignedOrder.run({
          message: draft.signedMessage.message,
          chain: draft.signedMessage.chain,
          quote: draft.signedMessage.quote,
          signature: draft.signedMessage.signature,
          permit: getPermitFromStore(draft),
        });
      }
    }
  );

  /**
   * Clear Quote error
   */
  GlobalStore.createReaction(
    store => {
      return {
        network: store.network,
        makerTokens: store.makerTokens,
        takerTokens: store.takerTokens,
        quoteError: store.quoteError,
      };
    },
    (watched, draft, original, lastWatched) => {
      if (!watched.quoteError) {
        return;
      }
      const networkChanged = watched.network.id !== lastWatched.network.id;
      if (networkChanged) {
        watched.quoteError = undefined;
      }
    }
  );

  /**
   * Clear Quote if we don't have a single taker token with a non-zero amount
   */
  GlobalStore.createReaction(
    store => store.takerTokens,
    (takerTokens, draft, original, lastWatched) => {
      if (!takerTokens.some(entry => entry.amount.value.gt(0))) {
        draft.quote = undefined;
        draft.wrapQuote = undefined;
      }
    }
  );

  /**
   * When tokens are changed while quoting - kick back to order entry and wipe out last quote
   */
  GlobalStore.createReaction(
    s => {
      return {
        makerTokens: s.makerTokens,
        takerTokens: s.takerTokens,
        modalSelector: s.modalSelector,
      };
    },
    (watched, draft, original, lastWatched) => {
      const changedMakerTokens = !areTokensIdentical(watched.makerTokens, lastWatched.makerTokens);
      const changedTakerTokens = !areTokensIdentical(watched.takerTokens, lastWatched.takerTokens);
      const takerArray = draft.takerTokens.map(e => e.tokenAddress);
      const makerArray = draft.makerTokens.map(e => e.tokenAddress);

      const isUnwrapping =
        takerArray.some(address =>
          isAddressEqual(address, getWrappedTokenAddress(draft.network))
        ) && makerArray.some(address => isAddressEqual(address, NATIVE_TOKEN_ADDRESS));
      const isWrapping =
        makerArray.some(address =>
          isAddressEqual(address, getWrappedTokenAddress(draft.network))
        ) && takerArray.some(address => isAddressEqual(address, NATIVE_TOKEN_ADDRESS));

      // Clears wrapQuote when changing a maker or taker so there's not a quote & wrapQuote in state at the same time
      if (!isWrapping && !isUnwrapping) {
        draft.wrapQuote = undefined;
      }

      if (changedMakerTokens || changedTakerTokens) {
        draft.orderStatus = OrderStatus.ENTRY;
        draft.quote = undefined;
        draft.quoteError = undefined;
        draft.permits = INITIAL_STATE.permits;
      }
    }
  );

  /** Remove "Are you sure you want to exit" alert */
  GlobalStore.createReaction(
    store => store.orderStatus,
    orderStatus => {
      if (orderStatus !== OrderStatus.COMPLETED && orderStatus !== OrderStatus.IN_PROGRESS) {
        window.onbeforeunload = null;
      }
    }
  );

  // Send GA wallet type info
  GlobalStore.createReaction(
    store => store.address,
    address => {
      let maxTries = 5;

      const sendWalletDataToGtm = () => {
        const account = getAccount();
        if (account.status === "reconnecting" || account.status === "connecting") {
          setTimeout(sendWalletDataToGtm, 1000);
          maxTries -= 1;
          if (maxTries === 0) return;
          return;
        }
        if (account.status === "disconnected" || account.connector == null || address === "") {
          return;
        }

        TagManager.dataLayer({
          dataLayer: {
            event: "wallet_type",
            address: account.address,
            wallet: account.connector.name,
          },
        });
      };
      sendWalletDataToGtm();
    }
  );

  // UAT-only: Change tokenlist source on toggle
  GlobalStore.createReaction(
    store => {
      return {
        isCustomTokenList: store.isCustomTokenList,
        customTokenListUrl: store.customTokenListUrl,
      };
    },
    (_, draft) => {
      draft.tokenLists = INITIAL_STATE.tokenLists;
      void getAllTokenLists.run();
    }
  );

  // Clears order error when any watched fields change
  GlobalStore.createReaction(
    store => {
      return {
        makerTokens: store.makerTokens,
        takerTokens: store.takerTokens,
        network: store.network,
        address: store.address,
        orderError: store.orderError,
      };
    },
    (watched, draft, original, lastWatched) => {
      const changedMakerTokens = !areTokensIdentical(watched.makerTokens, lastWatched.makerTokens);
      const changedTakerTokens = !areTokensIdentical(watched.takerTokens, lastWatched.takerTokens);
      const networkChanged = watched.network.id !== lastWatched.network.id;
      const addressChanged = watched.address !== lastWatched.address;
      if (
        draft.orderError !== undefined &&
        (changedMakerTokens || changedTakerTokens || networkChanged || addressChanged)
      ) {
        draft.orderError = undefined;
      }
    }
  );
  // Clear stored permits when the approval target changes
  GlobalStore.createReaction(
    store => store.quote,
    (watched, draft, original, lastWatched) => {
      if (lastWatched?.approvalTarget !== watched?.approvalTarget) {
        draft.permits = INITIAL_STATE.permits;
      }
    }
  );
};
